import _ from 'lodash'
import Backbone from 'backbone'

/**
 * A Cell within the CellLayout
 */
class Cell {

	constructor(opts = {}) {
		this.rowspan = _.isNil(opts.rowspan) ? 1 : opts.rowspan
		this.colspan = _.isNil(opts.colspan) ? 1 : opts.colspan
		this.data = opts.data || {}
		this.id = opts.id
		//track adjacent cells
		this.topCells = new Set()
		this.bottomCells = new Set()
		this.leftCells = new Set()
		this.rightCells = new Set()
	}

	toString() {
		return `cell id/row/colspan/width ${this.id}/${this.rowspan}/${this.colspan}`
	}
}

/**
 * Represents layout of cells that build the advance layout of the report
 * designer.
 *
 * The layout is built using a 2D array that forms a grid of "pixels" (spaces) in
 * the layout.  User content is placed in "cells" that can span multiple columns
 * or rows based on however the user wants to layout their data.  The pixels
 * define how much space a cell in the layout takes up.
 *
 */
class CellLayout {

	constructor(matrix = [[{id:1, rowspan:1, colspan:1}]]) {
		_.extend(this, Backbone.Events)
		if (!_.isArray(matrix)) {
			matrix = matrix.layoutMatrix
			if (_.isNil(matrix)) {
				matrix = [[{id:1, rowspan:1, colspan:1}]]
			}
		}
		this._cellById = {}
		this._nextId = 1
		this.layoutMatrix = [[]]
		_.each(matrix, (row, ri) => {
			this.layoutMatrix[ri] = []
			_.each(row, props => {
				this.layoutMatrix[ri].push(this._newCell(props))
			})
		})
		this._calculateLayout()
	}

	_newCell(props = {}) {
		if (_.isNil(props.id)) {
			props.id = this._nextId
		}
		let cell = this._cellById[props.id]
		if (!cell) {
			cell = new Cell(props)
		}
		this._cellById[cell.id] = cell
		this._nextId = Math.max(cell.id, this._nextId)+1
		return cell
	}

	/**
	 * Returns an array of cells that occupy the row index.  The array's length
	 * is the number of pixel within that row, where each index corresponds to
	 * a pixel in the matrix
	 */
	row(row) {
		return this.layoutMatrix[row]
	}

	/**
	 * Returns an array of cells that occupy the column index.  The array's length
	 * is the number of pixel within that column, where each index corresponds to
	 * a pixel in the matrix
	 */
	column(column) {
		let c = []
		_.each(this.layoutMatrix, row => {
			row[column] && c.push(row[column])
		})
		return c
	}

	cell(id) {
		return this._cellById[id]
	}

	/**
	 * Adds a row or column to the layout, returns the cell created
	 *
	 * @param [Boolean=true] horizontal true if add a row, false if column
	 */
	append(horizontal = true) {
		let row = this.layoutMatrix[0]
		let newCell = this._newCell()
		let prop = horizontal ? 'colspan' : 'rowspan'
		let size = horizontal ? row.length : this.layoutMatrix.length
		newCell[prop] = Math.max(size, 1)
		if (row.length == 0) {
			row.push(newCell)
		} else if (horizontal) {
			this.layoutMatrix.push(_.fill(new Array(size), newCell))
		} else {
			_.each(this.layoutMatrix, row => row.push(newCell))
		}
		this._calculateLayout()
		this.trigger('update:layout')
		return newCell
	}

	/**
	 * Splits the cell at specified row/column index, returns the cell created if any
	 *
	 * @param {Integer} cellId the cell id being split
	 * @param {boolean} [opts.horizontal=true] true to split the cell horizontally
	 * created a cell underneath the split cell, false to split the cell vertically
	 * and create a cell to the right of the split cell
	 */
	split(cellId, horizontal = true) {
		let cell = this._cellById[cellId]
		if (!cell) { return }

		//handle splitting even/odd spans
		let spanProperty = horizontal ? 'rowspan' : 'colspan'
		let span = cell[spanProperty]
		if ((span % 2) != 0) {
			//the span is not even, so we need to expand the matrix by first
			//finding all the cells that need expansion
			let start = horizontal ? cell.topRowIndex : cell.leftColumnIndex
			let end = horizontal ? cell.bottomRowIndex : cell.rightColumnIndex
			let expandIndexStart = start
			let expandIndexEnd = end
			this._range(start, end, horizontal, c => {
				expandIndexStart = Math.min(expandIndexStart, horizontal ? c.topRowIndex : c.leftColumnIndex)
				expandIndexEnd = Math.max(expandIndexEnd, horizontal ? c.bottomRowIndex : c.rightColumnIndex)
			})

			//expand their spans and rebuild the matrix
			this._range(expandIndexStart, expandIndexEnd, horizontal, c => c[spanProperty] *= 2)
			this._rebuild()
			cell = this._cellById[cellId]
			cell[spanProperty] = span
		} else {
			cell[spanProperty] = Math.floor(span/2)
		}

		//split the cell
		let newCell = this._newCell({rowspan: cell.rowspan,
			colspan: cell.colspan, data: _.cloneDeep(cell.data)})
		let start = horizontal ? cell.topRowIndex : cell.leftColumnIndex
		let end = horizontal ? cell.bottomRowIndex : cell.rightColumnIndex
		start = (end+start+1)/2
		for (let i=start;i<=end;i++) {
			let arr = horizontal ? this.layoutMatrix[i] : this.column(i)
			_.each(arr, (c, ci) => {
				if (cell === c) {
					if (horizontal) {
						this.layoutMatrix[i][ci] = newCell
					} else {
						this.layoutMatrix[ci][i] = newCell
					}
				}
			})
		}
		this._calculateLayout()
		this.trigger('update:layout')
		return newCell
	}

	/**
	 * Merges a source cell with it's target cell
	 *
	 * @param {Integer} sourceCellId the source
	 * @param {Integer} targetCellId the target
	 */
	merge(sourceCellId, targetCellId) {
		let sourceCell = this._cellById[sourceCellId]
		let targetCell = this._cellById[targetCellId]
		if (!sourceCell || !targetCell || targetCell === sourceCell) { return false }

		let merge = this.mergeTargets(sourceCell)
		if (!merge) { return false }

		if (merge.top == targetCellId || merge.bottom == targetCellId ||
			merge.left == targetCellId || merge.right == targetCellId) {
			for (let i=targetCell.topRowIndex; i<=targetCell.bottomRowIndex; i++) {
				for (let j=targetCell.leftColumnIndex; j<=targetCell.rightColumnIndex; j++) {
					this.layoutMatrix[i][j] = sourceCell
				}
			}
			if (merge.top == targetCellId || merge.bottom == targetCellId) {
				sourceCell.rowspan += targetCell.rowspan
			} else {
				sourceCell.colspan += targetCell.colspan
			}
			this._calculateLayout()
			this.trigger('update:layout')
			return true
		}
		return false
	}

	/**
	 * Copies a cell's content to another cell
	 *
	 * @param {Integer} sourceCellId the source
	 * @param {Integer} targetCellId the target
	 */
	copy(sourceCellId, targetCellId) {
		let sourceCell = this._cellById[sourceCellId]
		let targetCell = this._cellById[targetCellId]
		if (!sourceCell || !targetCell || targetCell === sourceCell) { return false }

		targetCell.data = _.cloneDeep(sourceCell.data)
		this.trigger('update:layout')
	}

	/**
	 * Deletes the cell with the given id
	 *
	 * @param {Integer} cellId the cell id
	 */
	delete(cellId) {
		let sourceCell = this._cellById[cellId]
		if (!sourceCell) { return }

		let newMatrix = []
		_.each(this.layoutMatrix, (row, ri) => {
			let currentRow = []
			_.each(row, cell => {
				if (cell !== sourceCell) {
					currentRow.push(cell)
				}
			})
			if (currentRow.length > 0) {
				newMatrix.push(currentRow)
			}
		})
		this.layoutMatrix = newMatrix
		delete this._cellById[cellId]
		this._calculateLayout()
		this.trigger('update:layout')
	}

	/**
	 * Sets/Gets the data for a given cell
	 *
	 * @param [{Integer}] cellId the cell id
	 * @param [{Object}] data if provided or null sets the data to that value,
	 * otherwise returns the data found at cell id, if no args passed returns
	 * a hash of cell data by their cell id
	 */
	data(cellId, data) {
		if (arguments.length == 0) {
			let dataByCellId = {}
			_.each(this._cellById, cell => {
				dataByCellId[cell.id] = cell.data
			})
			return dataByCellId
		}

		let sourceCell = this._cellById[cellId]
		if (!sourceCell) { return }
		if (_.isUndefined(data)) {
			return sourceCell.data
		}
		sourceCell.data = data
		this.trigger('update:data', data, cellId)
	}

	/**
	 * Returns valid merge targets for the give cell
	 *
	 * @param {Cell} cell the cell
	 * @return {Object|null} null if no valid target, or an object with
	 * top/bottom/left/right properties whose value is the cell id a valid
	 * merge target (if any)
	 */
	mergeTargets(cell) {
		let merge = {}
		//a valid target is a cell that shares their entire boundary against
		//the other cell who in turn also does the same
		let gatherMergeTargets = (set, prop, horizontal) => {
			if (set.size == 1) {
				let target = set.values().next().value
				let sameColumns = target.leftColumnIndex == cell.leftColumnIndex
					&& target.rightColumnIndex == cell.rightColumnIndex
				let sameRows = target.topRowIndex == cell.topRowIndex
					&& target.bottomRowIndex == cell.bottomRowIndex
				if ((horizontal && sameColumns) || (!horizontal && sameRows)) {
					merge[prop] = target.id
				}
			}
		}
		gatherMergeTargets(cell.topCells, 'top', true)
		gatherMergeTargets(cell.bottomCells, 'bottom', true)
		gatherMergeTargets(cell.leftCells, 'left')
		gatherMergeTargets(cell.rightCells, 'right')
		return _.isEmpty(merge) ? null : merge
	}

	/**
	 * Returns true if the cell spans the entire row or column
	 *
	 * @param {Cell} cell the cell
	 */
	deleteTargets(cell) {
		return (cell.colspan == this.colspanMax || cell.rowspan == this.layoutMatrix.length)
			&& this._cellById.length > 1	//don't allow empty layout
	}

	/**
	 * Returns the actions available for a given row/column index or a null
	 * if no actions are available
	 *
	 * @param {Integer} cellId the id of the cell
	 * @returns {Object} object with 'merge', 'delete', 'copy' properties
	 * indicating that action's availability
	 */
	actions(cellId) {
		let cell = this._cellById[cellId]
		if (!cell) { return {} }

		return {
			merge: this.mergeTargets(cell),
			delete: this.deleteTargets(cell),
			copy: _.size(this._cellById) > 1
		}
	}

	_calcColspanMax() {
		this.colspanMax = 0
		this._range(0, 0, true, cell => this.colspanMax += cell.colspan)
	}

	/**
	 * Computes properties of each cells required to properly build the layout
	 *
	 * @private
	 */
	_calculateLayout() {
		this._calcColspanMax()

		//reset each cell's row/column index
		this._eachCell(cell => {
			_.each(['topRowIndex','bottomRowIndex','leftColumnIndex','rightColumnIndex'], prop => cell[prop] = null)
			_.each(['topCells','bottomCells','leftCells','rightCells'], prop => cell[prop].clear())
		})

		//rebuild the pixel space to cell map and calculate the start/end of
		//the row/column index for each cell
		this.pixelSpace = {}
		this._eachPixel((pixel, cell, ri, ci) => {
			this.pixelSpace[pixel] = cell
			cell.topRowIndex = _.isNil(cell.topRowIndex) ? ri : cell.topRowIndex
			cell.bottomRowIndex = _.isNil(cell.bottomRowIndex) ? ri : Math.max(cell.bottomRowIndex, ri)
			cell.leftColumnIndex = _.isNil(cell.leftColumnIndex) ? ci : cell.leftColumnIndex
			cell.rightColumnIndex = _.isNil(cell.rightColumnIndex) ? ci : Math.max(cell.rightColumnIndex, ci)
		})

		//calculate the adjacent cells
		let addAdjacentCells = (set, start, end, arr) => {
			for (let i=start; i<=end; i++) {
				set.add(arr[i])
			}
		}
		this._eachCell(cell => {
			if (cell.topRowIndex > 0) {
				//add cells above cell
				addAdjacentCells(cell.topCells, cell.leftColumnIndex,
					cell.rightColumnIndex, this.row(cell.topRowIndex-1))
			}
			if (cell.bottomRowIndex < this.layoutMatrix.length-1) {
				//add cells below cell
				addAdjacentCells(cell.bottomCells, cell.leftColumnIndex,
					cell.rightColumnIndex, this.row(cell.bottomRowIndex+1))
			}
			if (cell.leftColumnIndex > 0) {
				//add cells left of cell
				addAdjacentCells(cell.leftCells, cell.topRowIndex,
					cell.bottomRowIndex, this.column(cell.leftColumnIndex-1))
			}
			if (cell.rightColumnIndex < this.layoutMatrix[0].length-1) {
				//add cells right of cell
				addAdjacentCells(cell.rightCells, cell.topRowIndex,
					cell.bottomRowIndex, this.column(cell.rightColumnIndex+1))
			}
		})
	}

	/**
	 * Rebuilds the matrix based on any colspan/rowspan changes to the cells
	 * @private
	 */
	_rebuild() {
		this._calcColspanMax()
		let spaceMatrix = [new Array(this.colspanMax)]
		this._eachCell(cell => {
			let columnCounter = null
			let currentUnfilledRow = 0
			for (let i=0;i<cell.rowspan;i++) {
				let row = spaceMatrix[currentUnfilledRow+i]
				//move row counter to the first row not completed full
				while (_.every(row, c => !_.isNil(c))) {
					row = spaceMatrix[++currentUnfilledRow]
					if (!row) {
						spaceMatrix[currentUnfilledRow] = new Array(this.colspanMax)
						row = spaceMatrix[currentUnfilledRow]
					}
				}

				if (columnCounter == null) {
					//move the column counter to the first empty space
					columnCounter = _.findIndex(row, c => _.isNil(c))
				}
				for (let j=0;j<cell.colspan;j++) {
					row[columnCounter+j]= cell
				}
			}
		})
		this.layoutMatrix = spaceMatrix
		this._calculateLayout()
	}

	/**
	 * Apply a function to each pixel in the layout.
	 *
	 * A pixel is the space within the matrix a cell can take up, it is the smallest
	 * unit in the layout and cannot be manipulated directly by users.  The order the
	 * pixels are iterated start from the top left most pixel, continuing to
	 * the farthest right then moving down a row and repeating to the right,
	 * ending at the bottom right corner.
	 *
	 * The function called is passed the pixel location, the occupying cell, and
	 * the row and column index the pixel is located within the matrix
	 *
	 * @param {Function} func the function to apply
	 * @private
	 */
	_eachPixel(func) {
		let pixel = 1
		_.each(this.layoutMatrix, (row, ri) => {
			_.each(row, (cell, ci) => {
				if (cell) {
					func(pixel++, cell, ri, ci)
				}
			})
		})
	}

	/**
	 * Apply a function to each cell in the layout.
	 *
	 * Cells are the containers of user content in the layout, these are the smallest
	 * units users can directly manipulate. The order the cells are iterated
	 * start from the top left most cell, continuing to the farthest right then
	 * moving down a row and repeating to the right, ending at the bottom right
	 * corner.
	 *
	 * @param {Function} func the function to apply
	 * @private
	 */
	_eachCell(func) {
		let processed = {}
		this._eachPixel((pixel, cell) => {
			if (processed[cell.id]) { return }
			processed[cell.id] = true
			func(cell)
		})
	}

	/**
	 * Apply a function to a range of cells in the layout
	 *
	 * @param {Integer} start The start of the range
	 * @param {Integer} end The end of the range (Inclusive)
	 * @param {Boolean} horizontal true if applying over rows, false if columns
	 * @param {Function} func the function to apply
	 * @private
	 */
	_range(start, end, horizontal, func) {
		let processed = {}
		for (let i = start; i <= end; i++) {
			let arr = horizontal ? this.layoutMatrix[i] : this.column(i)
			_.each(arr, cell => {
				if (processed[cell.id]) { return }
				processed[cell.id] = true
				func(cell)
			})
		}
	}

	/**
	 * Returns a HTML String of the table, optionally taking a function to
	 * output the contents of each cell
	 *
	 * @param {Function} [content=_.identity] Function to format the contents
	 * of each cell.
	 */
	html(content = (data) => {return data || ''}) {
		let html = '<table>'

		let maxPixelCount = _.size(this.pixelSpace)
		let processedNodes = {}
		for (let i=1; i<=maxPixelCount; i=i+this.colspanMax) {
			html += '<tr>'
			for (let j=0; j<this.colspanMax; j++) {
				let cell = this.pixelSpace[i + j]
				if (cell && !processedNodes[cell.id]) {
					let width = Math.trunc(cell.colspan/this.colspanMax * 100)
					html += `<td width="${width}%" rowspan="${cell.rowspan}" colspan="${cell.colspan}" data-cell-id="${cell.id}">${content(cell.data)}</td>`
					processedNodes[cell.id] = true
				}
			}
			html += '</tr>'
		}
		html += '</table>'
		return html
	}

	toJSON() {
		return { layoutMatrix: this.layoutMatrix }
	}

}

export default CellLayout
export { Cell }
CellLayout.__filename__ = 'reports/common/cell-layout.js'
